import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { Controller, useFormContext } from 'react-hook-form';
import { MINUTE_STEPS, WeekScheduleFormFields } from './const';
import { TimeRangeValue, WeekScheduleFormValues } from './week-schedule';

type TimeWindowValueWithIndex = TimeRangeValue & { index: number };

export interface TimeRangeInputProps {
  fieldName: WeekScheduleFormFields;
  items: TimeWindowValueWithIndex[];
  dayIndex: number;
  turnedOffDateLabel?: string;
  maxTimeWindows?: number;
  onToggleOn: (dayIndex: number) => void;
  onToggleOff: (dayIndex: number) => void;
  onTimeChange: (dayIndex: number, inputType: 'from' | 'to', time: dayjs.Dayjs | null) => void;
  onTimeAdd: (dayIndex: number) => void;
  onTimeDelete: (dayIndex: number) => void;
}

export const TimeRangeInput = ({
  fieldName,
  items,
  dayIndex,
  turnedOffDateLabel,
  maxTimeWindows,
  onTimeChange,
  onToggleOff,
  onToggleOn,
  onTimeAdd,
  onTimeDelete,
}: TimeRangeInputProps) => {
  const { getValues, control } = useFormContext<WeekScheduleFormValues>();

  const isDayEnabled = !!items?.length;
  const itemCount = items?.length ?? 0;
  const canAddMoreTimeWindows = !maxTimeWindows || itemCount < maxTimeWindows;

  const renderTimePickers = (it: TimeWindowValueWithIndex) => (
    <Stack direction="row" mb={2} spacing={1} key={it.id} alignItems="center">
      <FormControl>
        <Controller
          name={`${fieldName}.${it.index}.from`}
          control={control}
          render={({ field }) => (
            <TimePicker
              label="From"
              value={dayjs(field.value)}
              maxTime={dayjs(getValues(`${fieldName}.${it.index}.to`)).subtract(30, 'minute')}
              onChange={(time) => {
                onTimeChange(getValues(`${fieldName}.${it.index}.id`), 'from', time);
              }}
              timeSteps={{ minutes: MINUTE_STEPS }}
            />
          )}
        />
      </FormControl>
      <Box sx={{ width: '20px', textAlign: 'center' }}>-</Box>
      <FormControl>
        <Controller
          name={`${fieldName}.${it.index}.to`}
          control={control}
          render={({ field }) => (
            <TimePicker
              label="To"
              value={dayjs(field.value)}
              onChange={(time) => {
                onTimeChange(getValues(`${fieldName}.${it.index}.id`), 'to', time);
              }}
              timeSteps={{ minutes: MINUTE_STEPS }}
              minTime={dayjs(`${fieldName}.${it.index}.from`).add(30, 'minute')}
            />
          )}
        />
      </FormControl>
      <IconButton onClick={() => onTimeDelete(it.index)} aria-label="delete" size="small">
        <DeleteIcon />
      </IconButton>
    </Stack>
  );

  return (
    <Stack mb={2} gap={1}>
      <FormControlLabel
        sx={{
          alignSelf: 'flex-start',
        }}
        control={
          <Switch
            onChange={(e) => {
              if (!e.target.checked) {
                onToggleOff(dayIndex);
              } else {
                onToggleOn(dayIndex);
              }
            }}
            checked={isDayEnabled}
          />
        }
        label={dayjs().day(dayIndex).format('dddd')}
        slotProps={{
          typography: {
            variant: 'subtitle1',
            fontWeight: 'bold',
          },
        }}
      />
      {isDayEnabled ? (
        <Stack>
          {items?.map?.(renderTimePickers)}
          {canAddMoreTimeWindows ? (
            <Button
              onClick={() => onTimeAdd(dayIndex)}
              variant="text"
              aria-label="add"
              startIcon={<AddCircleOutlineIcon />}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add time window
            </Button>
          ) : null}
        </Stack>
      ) : (
        <Stack justifyContent="center" height="56px">
          <Typography variant="body2">{turnedOffDateLabel || 'Outside of hours'}</Typography>
        </Stack>
      )}
    </Stack>
  );
};
